import { Injectable } from '@angular/core';

import { FixtureType } from '@cds/betting-offer';
import { CountsFixturesResponse } from '@cds/betting-offer/domain-specific';
import { FixtureState, OfferMapping, SortByCriteria } from '@cds/query-objects';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { SportsCacheService } from '@frontend/sports/betting-offer/feature/sports-cache';
import { LiveNavigationConfig } from '@frontend/sports/common/client-config-data-access';
import { sportModelMethods } from '@frontend/sports/common/core/data-access/sport-model';
import { toString } from 'lodash-es';
import { BehaviorSubject, Observable, firstValueFrom, map, of, tap } from 'rxjs';

import { BaseTreeItem, GroupedEvents, SelectionInfo, TreeItem } from '../../event-details-common/event-details-common.models';
import { getLeagueId } from '../../event-details-common/event-details-common.utils';
import { NavigationEventMapperService } from '../navigation-mapper/navigation-event-mapper.service';
import { NavigationMapperService } from '../navigation-mapper/navigation-mapper.service';
import { LiveEventsNavigationStateService } from './live-events-navigation-state.service';
import { LeagueGroup, LiveEventsNavigationTreeService } from './live-events-navigation-tree.service';

@Injectable({ providedIn: 'root' })
export class LiveEventsNavigationService {
    selectedEvent: EventModel;
    visibleEvents?: GroupedEvents;

    private _tree$ = new BehaviorSubject<BaseTreeItem | undefined>(undefined);
    private defaultEvent: EventModel | undefined;

    constructor(
        private treeService: LiveEventsNavigationTreeService,
        private liveNavConfig: LiveNavigationConfig,
        private stateService: LiveEventsNavigationStateService,
        private mapperService: NavigationMapperService,
        private eventMapperService: NavigationEventMapperService,
        private sportsCacheService: SportsCacheService,
        private bettingContent: BettingOfferApi,
    ) {}

    async initFromEvent(event: EventModel): Promise<BaseTreeItem | undefined> {
        this.setSelectedEvent(event);

        // already initialized with default event
        if (this.defaultEvent && this.defaultEvent.id === event.id) {
            if (!this.tree) {
                await this.initFromDefaultEvent();
            }

            return this.tree;
        }

        let tree = await this.loadTree();
        tree = this.initState(tree, event);
        const leaguesToFetch = this.treeService.getVisibleLeagues(tree);
        this.visibleEvents = await firstValueFrom(this.getGroupedEvents(leaguesToFetch.map((p) => p.leagueId)));

        this._tree$.next(tree);

        return tree;
    }

    async initFromDefaultEvent(): Promise<EventModel> {
        let tree = await this.loadTree();
        tree = this.initState(tree, undefined);

        const leaguesToFetch = this.treeService.getVisibleLeagues(tree);
        if (!this.stateService.state.isEmpty()) {
            const firstLeague = this.treeService.getFirstLeagueId(tree);
            const leagueGroup: LeagueGroup = {
                leagueId: firstLeague,
                virtualLeagueId: 0,
            };
            leaguesToFetch.push(leagueGroup);
        }

        this.visibleEvents = await firstValueFrom(this.getGroupedEvents(leaguesToFetch.map((p) => p.leagueId)));
        this.defaultEvent = this.getDefaultEvent(
            this.visibleEvents,
            leaguesToFetch.filter((league) => this.visibleEvents?.[league.leagueId])[0]?.leagueId,
        );

        this._tree$.next(tree);

        return this.defaultEvent;
    }

    refreshTree(data: CountsFixturesResponse): void {
        this.fetchTree(data);
    }

    reloadTree(data: CountsFixturesResponse): void {
        this.fetchTree(data, true);
    }

    toggleSport(item: TreeItem): void {
        const { leaguesToFetch, tree } = this.treeService.toggleSport(this.tree!, item);
        this._tree$.next(tree);
        this.loadEventsForLeagues(leaguesToFetch).subscribe();
    }

    toggleLeague(item: TreeItem): void {
        const { leaguesToFetch, tree } = this.treeService.toggleLeague(this.tree!, item);
        this._tree$.next(tree);
        this.loadEventsForLeagues(leaguesToFetch).subscribe();
    }

    toggleMarkets(sport: TreeItem): void {
        this._tree$.next(this.treeService.toggleMarkets(this.tree!, sport));
    }

    toggleFavourites(item: TreeItem): void {
        this._tree$.next(this.treeService.toggleFavourites(this.tree!, item));
    }

    toggleFavouriteLeague(item: TreeItem): void {
        this._tree$.next(this.treeService.toggleFavouriteLeague(this.tree!, item));
    }

    async expandEvent(event: EventModel): Promise<void> {
        const toggleResult = this.treeService.expandEvent(this.tree!, event);
        this._tree$.next(toggleResult.tree);
        await firstValueFrom(this.loadEventsForLeagues(toggleResult.leaguesToFetch));
    }

    selectEvent(event: EventModel): void {
        this._tree$.next(this.treeService.selectEvent(this.tree!, event));
        this.setSelectedEvent(event);
    }

    updateEvents(events: EventModel[]): void {
        this._tree$.next(this.treeService.updateEvents(this.tree!, events));
    }

    refreshFavouritesTreeItem(): void {
        this._tree$.next(this.treeService.refreshFavouritesTreeItem(this.tree!));
    }

    loadVisibleLeagues(): Observable<BaseTreeItem | undefined> {
        return this.loadEventsForLeagues(this.treeService.getVisibleLeagues(this.tree!));
    }

    isSelected(eventId: string): boolean {
        return this.treeService.isSelected(eventId);
    }

    reset(): void {
        this._tree$.next(undefined);
        delete this.visibleEvents;
    }

    get tree$(): Observable<BaseTreeItem | undefined> {
        return this._tree$.asObservable();
    }

    private get tree(): BaseTreeItem | undefined {
        return this._tree$.value;
    }

    private async loadTree(): Promise<BaseTreeItem> {
        const liveSportTree = await firstValueFrom(this.sportsCacheService.getLiveSportList());

        return this.mapperService.mapTree(liveSportTree);
    }

    private fetchTree(data: CountsFixturesResponse, forceReload?: boolean): void {
        const newTree = this.mapperService.mapTree(sportModelMethods.fromTag(data.counts), getLeagueId(this.selectedEvent), false);
        const groupedEvents: GroupedEvents = this.eventMapperService.mapFixtures(data.fixtures);
        this._tree$.next(this.treeService.refreshTree(this.tree!, newTree, groupedEvents, forceReload));
    }

    private initState(tree: BaseTreeItem, selectedEvent: EventModel | undefined): BaseTreeItem {
        const state = this.stateService.state;
        const selectedSportId = selectedEvent ? selectedEvent.sport.id : this.treeService.getFirstSportId(tree);
        const selectedLeagueId = selectedEvent ? getLeagueId(selectedEvent) : this.treeService.getFirstLeagueId(tree);

        if (state.isEmpty()) {
            const sport = tree.children.find((s) => s.id === selectedSportId);
            if (sport && this.exceedsTreshold(sport)) {
                sport.children.forEach((l) => state.collapsedLeagues.add(l.id));
            }
        }

        state.expandedSports.add(selectedSportId);
        state.collapsedLeagues.delete(selectedLeagueId);
        this.stateService.storeState();

        return this.mapperService.updateTreeState(tree, state);
    }

    private exceedsTreshold(sport: TreeItem): boolean {
        return <number>sport.count > this.liveNavConfig.treshold;
    }

    private loadEventsForLeagues(leagues: LeagueGroup[]): Observable<BaseTreeItem | undefined> {
        if (!leagues?.length) {
            return of(undefined);
        }

        return this.getGroupedEvents(leagues.map((p) => p.leagueId)).pipe(
            map((events) => this.treeService.updateTree(this.tree!, leagues, events)),
            tap((tree) => this._tree$.next(tree)),
        );
    }

    private getGroupedEvents(leagueIds: Iterable<number>): Observable<GroupedEvents> {
        return this.bettingContent
            .getFixtureList({
                fixtureTypes: FixtureType.Standard,
                state: FixtureState.Live,
                offerMapping: OfferMapping.MainMarkets,
                sortBy: SortByCriteria.FixtureStage,
                competitionIds: toString(leagueIds),
            })
            .pipe(
                map((page) => {
                    return this.eventMapperService.mapFixtures(page);
                }),
            );
    }

    private getDefaultEvent(events: GroupedEvents, firstLeague: number): EventModel {
        const selection = this.stateService.state.selection;
        if (selection && events[selection.leagueId]) {
            const selectedEvent = events[selection.leagueId].find((e) => e.id === selection.eventId);
            if (selectedEvent) {
                return selectedEvent;
            }
        }

        return events[firstLeague][0];
    }

    private setSelectedEvent(event: EventModel): void {
        this.selectedEvent = event;
        this.treeService.setSelectionInfo(event);
        this.stateService.state.selection = new SelectionInfo(event);
    }
}
